import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCurrentClient } from 'src/clients/context/CurrentClientProvider';
import { api } from 'src/common/utils/api';
import { CashflowFrequency } from '../utils';

export type ExpenditureCategory =
  | 'Insurance'
  | 'Cable'
  | 'Online'
  | 'Service'
  | 'Other bills'
  | 'Telephone'
  | 'Utilities'
  | 'Office supplies'
  | 'Advertising'
  | 'Business'
  | 'Postage'
  | 'Printing'
  | 'Office maintenance'
  | 'Wages'
  | 'ATM'
  | 'Entertainment'
  | 'Restaurants'
  | 'Hobbies'
  | 'Dues'
  | 'Gambling'
  | 'Charitable'
  | 'Gifts'
  | 'Groceries'
  | 'General'
  | 'Healthcare'
  | 'Personal'
  | 'Child'
  | 'Clothing'
  | 'Education'
  | 'Home Maintenance'
  | 'Home Improvement'
  | 'Pets'
  | 'Electronic'
  | 'Mixed'
  | 'Uncategorized'
  | 'Other'
  | 'Cheques'
  | 'Rent'
  | 'Loans'
  | 'Mortgages'
  | 'Taxes'
  | 'Automotive'
  | 'Gasoline'
  | 'Travel';

export type Expenditure = {
  id: string;
  /** Shared by the one-row-per-member records of an item entered once for several members. */
  groupId?: string | null;
  description: string | null;
  category: ExpenditureCategory | null;
  amount: number | null;
  frequency: CashflowFrequency | null;
  clientId: string;
};

export type EditableExpenditure = Omit<Expenditure, 'id'> & {
  id?: string;
};

type ExpenditureInput = Omit<EditableExpenditure, 'clientId'>;

type UseExpendituresReturnType = {
  isLoading: boolean;
  expendituresData: Expenditure[] | undefined;
  upsertExpenditures: (items: ExpenditureInput[]) => Promise<void>;
  deleteExpenditures: (ids: string[]) => Promise<void>;
};

export default function useExpenditures(): UseExpendituresReturnType {
  const { clientId } = useCurrentClient();
  const queryClient = useQueryClient();
  const queryKey = ['clients', clientId, 'expenditures'];

  const { data: expendituresData, isLoading } = useQuery<Expenditure[]>({
    queryKey,
    enabled: !!clientId,
  });

  const { mutateAsync: upsertMutation } = useMutation({
    mutationFn: (items: ExpenditureInput[]) =>
      api.put(
        `clients/${clientId}/expenditures/batch`,
        items.map(item => ({ ...item, clientId })),
      ),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  const upsertExpenditures = async (items: ExpenditureInput[]) => {
    await upsertMutation(items);
  };

  const { mutateAsync: deleteMutation } = useMutation({
    mutationFn: (ids: string[]) => api.post(`clients/${clientId}/expenditures/delete-request`, ids),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  const deleteExpenditures = async (ids: string[]) => {
    await deleteMutation(ids);
  };

  return {
    isLoading,
    expendituresData,
    upsertExpenditures,
    deleteExpenditures,
  };
}
